import range from 'lodash/range'

/**
 * Checks if the value passes the filter based on equality comparison.
 * Returns true if the value satisfies the filter, otherwise false.
 * @param {object} filter - The filter object containing a `value` property.
 * @param {Array<string>} value - The array of values to compare.
 * @param {boolean} hideAction - Indicates whether an action should be hidden.
 * @returns {boolean} - True if the action should be hidden, otherwise false.
 */
export const equalsOperation = function (filter, value, hideAction) {
    // Check to filter on an empty filter (eg: tier="")
    if (!filter.value) {
        // We always expect an array for `value`, we use the "some" check to check if the array only consists of empty strings
        // The "some" function returns false if there are only empty strings and true if it finds strings that are not empty
        if (value.some(Boolean)) {
            hideAction = true
        }
    } else {
        // Split on '||' (OR operator) if it's existing in the filter
        const filterValueAsArray = filter.value.includes('||')
            ? filter.value.split('||')
            : [].concat(filter.value)

        // Create sets for efficient lookup
        const filterValueSet = new Set(
            filterValueAsArray.map((val) => {
                const upperVal = val.toUpperCase()
                if (upperVal === 'TRUE') {
                    return 'TRUE'
                }
                if (upperVal === 'FALSE') {
                    return 'FALSE'
                }
                return val.toLowerCase()
            })
        )
        const valueSet = new Set(
            value.map((val) => {
                if (val === true) {
                    return 'TRUE'
                }
                if (val === false) {
                    return 'FALSE'
                }
                return val?.toLowerCase()
            })
        )

        // Check if there's any common element
        const commonElementsExist = [...filterValueSet].some((val) =>
            valueSet.has(val)
        )
        if (!commonElementsExist) {
            hideAction = true
        }
    }
    return hideAction
}

/**
 * Compares the item to the filter values and returns true if any value is "like" the item.
 * Supports || (OR) and && (AND) operators within the filter value.
 * - OR (||): Any of the values must match (e.g., "SHA||SAH" matches if value contains SHA OR SAH)
 * - AND (&&): All of the values must match (e.g., "Linux&&Server" matches if value contains Linux AND Server)
 * @param {object} filter - The filter object containing a `value` property.
 * @param {Array<string>} value - The array of values to compare.
 * @param {boolean} hideAction - Indicates whether an action should be hidden.
 * @returns {boolean} - True if the action should be hidden, otherwise false.
 */
export const likeOperation = function (filter, value, hideAction) {
    const valueAsArray = value.map((val) => val?.toLowerCase())

    // Check if the filter uses && (AND) operator - takes precedence over ||
    if (filter.value.includes('&&')) {
        // Split on '&&' for AND logic - ALL values must match
        const filterValueAsArray = filter.value
            .split('&&')
            .map((val) => val.toLowerCase())

        // Check if ALL filter values are contained in ANY of the device values
        const allFilterValuesMatch = filterValueAsArray.every((filterVal) =>
            valueAsArray.some((val) => val?.includes(filterVal))
        )

        if (!allFilterValuesMatch) {
            hideAction = true
        }
    } else if (filter.value.includes('||')) {
        // Split on '||' for OR logic - ANY value must match
        const filterValueAsArray = filter.value
            .split('||')
            .map((val) => val.toLowerCase())

        // Check if ANY filter value is similar to any of the elements of `value` (case-insensitive)
        const matchingElementsExist = filterValueAsArray.some((filterVal) =>
            valueAsArray.some((val) => val?.includes(filterVal))
        )

        if (!matchingElementsExist) {
            hideAction = true
        }
    } else {
        // No operator, single value
        const filterValueLower = filter.value.toLowerCase()
        const matchingElementsExist = valueAsArray.some((val) =>
            val?.includes(filterValueLower)
        )

        if (!matchingElementsExist) {
            hideAction = true
        }
    }

    return hideAction
}

/**
 * Checks if none of the values contain any of the filter values.
 * Supports || (OR) and && (AND) operators within the filter value.
 * - OR (||): None of the values should be present (e.g., "SHA||SAH" hides if value contains SHA OR SAH)
 * - AND (&&): All of the values must be absent (e.g., "Linux&&Server" hides if value contains both Linux AND Server)
 * Returns true if none of the values contain any of the filter values (i.e., the action should be hidden).
 * @param {object} filter - The filter object containing a `value` property.
 * @param {Array<string>} value - The array of values to compare.
 * @param {boolean} hideAction - Indicates whether an action should be hidden.
 * @returns {boolean} - True if the action should be hidden, otherwise false.
 */
export const notLikeOperation = function (filter, value, hideAction) {
    // Ensure filter.value is not null or undefined
    if (!filter.value) {
        console.error(`Invalid filter value: ${filter.value}`)
        return hideAction // Return the current hideAction state without modifying it
    }

    const valueAsArray = value.map((val) => val?.toLowerCase())

    // Check if the filter uses && (AND) operator - takes precedence over ||
    if (filter.value.includes('&&')) {
        // Split on '&&' for AND logic
        const filterValueAsArray = filter.value
            .split('&&')
            .map((val) => val.toLowerCase())

        // For "does not contain" with AND, hide the action if ALL filter values are found
        const allFilterValuesFound = filterValueAsArray.every((filterVal) =>
            valueAsArray.some((val) => val?.includes(filterVal))
        )

        if (allFilterValuesFound) {
            hideAction = true
        }
    } else if (filter.value.includes('||')) {
        // Split on '||' for OR logic
        const filterValueAsArray = filter.value
            .split('||')
            .map((val) => val.toLowerCase())

        // For "does not contain" with OR, hide the action if ANY filter value is found
        const anyFilterValueFound = filterValueAsArray.some((filterVal) =>
            valueAsArray.some((val) => val?.includes(filterVal))
        )

        if (anyFilterValueFound) {
            hideAction = true
        }
    } else {
        // No operator, single value
        const filterValueLower = filter.value.toLowerCase()
        const filterValueFound = valueAsArray.some((val) =>
            val?.includes(filterValueLower)
        )

        if (filterValueFound) {
            hideAction = true
        }
    }

    return hideAction
}

/**
 * Compares an array of values to a filter and returns true or false based on the not equals check.
 * @param {object} filter - The filter object containing a `value` property.
 * @param {Array<string>} value - The array of values to compare.
 * @param {boolean} hideAction - Indicates whether an action should be hidden.
 * @returns {boolean} - True if the action should be hidden, otherwise false.
 */
export const notEqualsOperation = function (filter, value, hideAction) {
    // Check to filter on an empty filter (eg: tier="")
    if (!filter.value) {
        // We always expect an array for `value`, we use the "some" check to check if the array only consists of empty strings
        // The "some" function returns false if there are only empty strings and true if it finds strings that are not empty
        if (!value.some(Boolean)) {
            hideAction = true
        }
    } else {
        // Split on '&&' (AND operator) if it's existing in the filter
        let filterValueAsArray = filter.value.includes('&&')
            ? filter.value.split('&&')
            : [].concat(filter.value)

        if (filterValueAsArray.length === 1)
            // Try splitting on '||' (OR operator) as in an old format '||' is used as an AND operator.
            // This kind of usage is becoming deprecated, but it still has to be supported for now.
            filterValueAsArray = filter.value.includes('||')
                ? filter.value.split('||')
                : [].concat(filter.value)

        // Create sets for efficient lookup
        const filterValueSet = new Set(
            filterValueAsArray.map((val) => {
                const upperVal = val.toUpperCase()
                if (upperVal === 'TRUE') {
                    return 'TRUE'
                }
                if (upperVal === 'FALSE') {
                    return 'FALSE'
                }
                return val.toLowerCase()
            })
        )
        const valueSet = new Set(
            value.map((val) => {
                if (val === true) {
                    return 'TRUE'
                }
                if (val === false) {
                    return 'FALSE'
                }
                return val?.toLowerCase()
            })
        )

        // Check if there's any common element
        const commonElementsExist = [...filterValueSet].some((val) =>
            valueSet.has(val)
        )
        const shouldHideNullValues = filterValueSet.has('')
        const foundNullValues = valueSet.has(undefined)
        if (commonElementsExist || (shouldHideNullValues && foundNullValues)) {
            hideAction = true
        }
    }
    return hideAction
}

/**
 * Checks the action on all its filters and filters out the action based on the operations bound in the filters list
 * @param action
 * @param filters
 * @returns {Boolean}
 */
export const checkDeviceFilters = function (action, filters) {
    const allowedFilterLabelMapping = {
        resolver_group: 'resolver_group',
        calculated_location: 'availability_zones',
        solution_ci: 'ci',
        solution_name: 'name',
        class: 'cmdb_class',
        ci: 'ci',
        hostname: 'name',
        environment: 'cgk_environment',
        role: 'role',
        customer_environment: 'environment',
        reason_no_dr: 'dr_reason_no',
        drp_method: 'drp_method',
        dr_active: 'dr_active',
        tier: 'tier',
        'part_of_virp-pg': 'virp_pg',
        dr_solution_name: 'dr_solution_name',
        dr_solution_ci: 'dr_solution_ci',
    }

    let hideAction = false

    // Filter out all not allowed filters and actions without details since we have to filter on those
    filters = filters.filter((item) => {
        return (
            item.label in allowedFilterLabelMapping &&
            this.ciToUid[action.device]?.details
        )
    })

    filters.forEach((item) => {
        // If a solution filter is active but the action is not a solution, we should hide it no matter what
        if (
            (item.label === 'solution_ci' || item.label === 'solution_name') &&
            this.ciToUid[action.device]?.details.solution === false
        ) {
            hideAction = true
        }
        const filterValue =
            [
                this.ciToUid[action.device]?.details?.[
                    allowedFilterLabelMapping[item.label]
                ],
            ]?.flat() ?? []

        try {
            switch (item.operation) {
                case '=':
                    hideAction = this.equalsOperation(
                        item,
                        filterValue,
                        hideAction
                    )
                    break
                case '~':
                    hideAction = this.likeOperation(
                        item,
                        filterValue,
                        hideAction
                    )
                    break
                case '<>':
                case '!=':
                    hideAction = this.notEqualsOperation(
                        item,
                        filterValue,
                        hideAction
                    )
                    break
                case '!~':
                    hideAction = this.notLikeOperation(
                        item,
                        filterValue,
                        hideAction
                    )
                    break
                default:
                    break
            }
        } catch (error) {
            console.error(
                `Error applying filter: ${JSON.stringify(
                    item
                )} on action: ${JSON.stringify(action)}`
            )
            throw error
        }
    })

    return hideAction
}

export const filterOutBusinessSolutionChildren = function (action) {
    if (action && action.deviceClass === 'Business Solution') {
        // Always filter out business solution children
        const children = action.treeChildren || []
        children.forEach((child) => {
            const childAction = this.actionsMap[child]
            if (childAction) {
                childAction.filteredByAction = true
            }
        })
    } else {
        // Check the children
        const children = action.treeChildren || []
        children.forEach((child) => {
            const childAction = this.actionsMap[child]
            if (childAction) {
                this.filterOutBusinessSolutionChildren(childAction)
            }
        })
    }
}

export const updateFilteredStateOnChildren = function (action, totalFilters) {
    if (action) {
        const prevFilters = Array.from(totalFilters)
        if (action.filters) {
            action.filters.forEach((actionFilter) => {
                // Check that there is no filter with the same label already.
                // Filters on higher levels are taken into consideration instead of lower level filters.
                if (
                    !totalFilters.some(
                        (filter) => filter.label === actionFilter.label
                    )
                ) {
                    totalFilters.push(actionFilter)
                }
            })
        }
        const conditionsToCheckFilters = [
            (action) =>
                action.treeChildren.length === 0 &&
                action.deviceClass !== 'DR Solution',
            (action) => action.deviceClass === 'Business Solution',
        ]
        if (
            !action.filters?.length && // no matter what, the action on which the filters are set should NOT be filtered out
            conditionsToCheckFilters.some((condition) => condition(action))
        ) {
            if (this.checkDeviceFilters(action, totalFilters)) {
                this.actionsMap[action.uid].filteredByAction = true
            }
        } else {
            const children = action.treeChildren || []
            children.forEach((child) => {
                const childAction = this.actionsMap[child]
                if (childAction) {
                    totalFilters = this.updateFilteredStateOnChildren(
                        childAction,
                        totalFilters
                    )
                }
            })
        }
        return prevFilters
    }
}

export const propagateFilteredStateUp = function (id) {
    let action = this.actionsMap[id]

    if (!action) {
        return
    }

    // first we will do the propagation to the children
    // they need to be set to filteredByAction = true (if matching) for the filter to work
    for (let childId of action.treeChildren) {
        this.propagateFilteredStateUp(childId)
    }

    // we don't meddle if the action is already filtered or if it has filters itself
    // If a Business solution is not filtered itself. It's children will always be filtered out so don't always hide this specific case
    if (
        action.filters === null &&
        !action.filteredByAction &&
        !(action.deviceClass === 'Business Solution')
    ) {
        if (action.children.length) {
            action.filteredByAction = action.children.every(
                (cId) =>
                    this.actionsMap[cId] &&
                    this.actionsMap[cId].filteredByAction
            )
        } else {
            // DR solution are normally not affected by filtering, but they must be cleaned up if they are empty
            action.filteredByAction = action.deviceClass === 'DR Solution'
        }
    }
}

export const propagateFilteredStateDown = function (id) {
    let action = this.actionsMap[id]

    if (!action) {
        return
    }

    action.filteredByAction = true

    for (let childId of action.treeChildren) {
        this.propagateFilteredStateDown(childId)
    }
}

/**
 * Recursively collects actions with filters from an action and its descendants
 * Stops collecting from a branch if an action with filters is encountered
 */
const collectActionsWithFilters = function (actionId, actionsMap) {
    const action = actionsMap[actionId]
    const actionsWithFilters = []

    if (!action) {
        return actionsWithFilters
    }

    // If action has filters, add it and stop here
    if (action.filters?.length) {
        actionsWithFilters.push(actionId)
        return actionsWithFilters
    }

    // If no filters on current action, recursively collect from children
    const children = action.treeChildren || []
    children.forEach((childId) => {
        const childActionsWithFilters = collectActionsWithFilters(
            childId,
            actionsMap
        )
        actionsWithFilters.push(...childActionsWithFilters)
    })

    return actionsWithFilters
}

export const applyRunbookFilters = function () {
    const uids = range(1, this.lastUid)
    const buildDepth0Actions = (currentTopActionId, currentTopActions) => {
        let updatedTopActions = currentTopActions
            ? [currentTopActionId, ...currentTopActions]
            : [currentTopActionId]
        const currentTopAction = this.actionsMap[currentTopActionId]
        if (
            currentTopAction &&
            currentTopAction.nextSibling &&
            this.actionsMap[currentTopAction.nextSibling]
        ) {
            updatedTopActions = buildDepth0Actions(
                currentTopAction.nextSibling,
                updatedTopActions
            )
        }
        return updatedTopActions
    }

    if (this.topAction) {
        const actionsWithFilters = []

        // Get all depth0 actions and collect filters from them and their descendants
        const depth0Actions = buildDepth0Actions(this.topAction)
        depth0Actions.forEach((actionId) => {
            const actionFilters = collectActionsWithFilters(
                actionId,
                this.actionsMap
            )
            actionsWithFilters.push(...actionFilters)
            // Hide Business Solution children for each depth0 action
            // This must be done irrespectively of whether there's a filter on those
            const actionToFilter = this.actionsMap[actionId]
            if (actionToFilter) {
                this.filterOutBusinessSolutionChildren(actionToFilter)
            }
        })

        // the children of actions with Filters are the starting point
        // these are normally:
        // * endpoints, defined as "actions without children"
        // * actions with device class = "Business Solution" (which may or may not be endpoints)
        // we are going to:
        // 1- Propagate the filtering down; this will hide actions below hidden actions
        //    * Concretely this means actions below hidden "Business Solution" will be hidden
        // 2- Propagate the filtering up; this will hide actions where either of these conditions is fulfilled
        //    * all children have been hidden
        //    * there is no associated device
        actionsWithFilters.forEach((actionId) => {
            const actionToUpdate = this.actionsMap[actionId]
            if (actionToUpdate) {
                this.updateFilteredStateOnChildren(actionToUpdate, [])
                this.propagateFilteredStateUp(actionId)
            }
        })
    }

    const filteredActionsWithChildren = uids.filter((uid) => {
        const action = this.actionsMap[uid]
        if (!action) {
            return false
        }
        return action.filteredByAction && action.treeChildren.length
    })
    filteredActionsWithChildren.forEach((uid) =>
        this.propagateFilteredStateDown(uid)
    )
}
